import React, { useState, useEffect, useMemo } from 'react'
import type { Listing, ProductType } from '@adc/sdk/types'
import { hooks, constants } from '@adc/ui-core'
import ListingCarousel from '../listingCarousel'
import { getMarketingListings } from './providers'

const { colorVars } = constants

interface AnalyticsActions {
  next: () => void
  back: () => void
  save: () => void
  click: () => void
}

interface ArrowIconPropsSettings {
  [key: string]: unknown
}

export type QueryType =
  | 'all'
  | 'land'
  | 'noLand'
  | 'remoteBid'
  | 'firstLook'
  | 'privateSellerLand'
  | 'privateSellerNoLand'
  | 'privateSellerAll'

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface ListingCarouselFilters {
  state?: string
  tags?: string[]
  productTypes?: ProductType[]
}

export interface ListingCarouselEmbeddedProps {
  queryType?: QueryType
  showcaseListings?: string[]
  coordinates: Coordinates
  elmId: string
  showSave: boolean
  onSaveToggle?: (...args: unknown[]) => unknown
  settings?: Record<string, unknown>
  analytics?: AnalyticsActions
  arrowIconPropsSettings?: ArrowIconPropsSettings
  featureFlags?: Record<string, unknown>
  targetHost?: string
  filters?: ListingCarouselFilters
}

export const ListingCarouselEmbedded: React.FC<
  ListingCarouselEmbeddedProps
> = ({
  queryType,
  showcaseListings,
  coordinates,
  elmId,
  onSaveToggle,
  settings,
  analytics,
  arrowIconPropsSettings,
  featureFlags,
  showSave = false,
  targetHost = 'https://www.auction.com',
  filters = {},
}) => {
  const [listings, setListings] = useState<Listing[]>([])
  const isMounted: boolean = hooks.useIsMounted() // eslint-disable-line
  const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]) // eslint-disable-line

  useEffect(() => {
    coordinates?.latitude &&
      coordinates?.longitude &&
      getMarketingListings({
        queryType,
        showcaseListings,
        coordinates,
        filters: memoizedFilters,
      })
        .then(({ data, errors }) => {
          if (errors && errors.length && !data?.content?.length) {
            throw errors[0]
          }
          const listings = ((data?.content || []) as Listing[]).filter(Boolean)
          /**
           * Point the PDP links to the target host (auction.com for now) instead of the current host
           */
          listings.forEach(listing => {
            listing.listing_page_path = `${targetHost}${listing.listing_page_path}`
          })
          return setListings(listings)
        })
        .catch((err: unknown) => err)
  }, [coordinates, memoizedFilters, queryType, showcaseListings, targetHost])

  const renderCarousel = isMounted
  return !renderCarousel ? null : (
    <div style={{ width: '100vw' }}>
      {listings?.length === 0 && (
        <div
          style={{
            fontSize: '16px',
            color: colorVars.utkColorGrayMedium,
            textAlign: 'center',
          }}
        >
          Featured listings coming soon...
        </div>
      )}
      {listings?.length > 0 && (
        <ListingCarousel
          listings={listings}
          analytics={analytics}
          showSave={showSave}
          elmId={elmId}
          arrowIconPropsSettings={arrowIconPropsSettings}
          featureFlags={featureFlags}
          onSaveToggle={onSaveToggle}
          settings={settings}
        />
      )}
    </div>
  )
}
